/**
 * Account Aggregator Edit Drawer
 *
 * Allows updating branding and onboarding settings.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Drawer, DrawerContent, DrawerHeader, DrawerOverlay, Button, InputField, Toggle } from 'equal-ds-ui';
import { Edit3 } from 'lucide-react';
import type {
  AccountAggregatorEditDrawerProps,
  UpdateAccountAggregatorPayload,
} from '../../../types/account-aggregator';

interface FormState {
  logoUrl: string;
  webRedirectionSecret: string;
  isSecretEditable: boolean;
  aaOnboard: boolean;
  channelStatus: boolean;
}

interface FormErrors {
  logoUrl?: string;
  webRedirectionSecret?: string;
}

const LOGO_URL_REGEX = /^[\w\-._~:/?#[\]@!$&'()*+,;=.]{2,1000}$/;
const SECRET_REGEX = /^[a-zA-Z0-9\-_.@#?$&~]{2,30}$/;

export const AccountAggregatorEditDrawer: React.FC<AccountAggregatorEditDrawerProps> = ({
  isOpen,
  record,
  onSubmit,
  onClose,
  isSubmitting,
}) => {
  const [formState, setFormState] = useState<FormState>({
    logoUrl: '',
    webRedirectionSecret: '',
    isSecretEditable: false,
    aaOnboard: false,
    channelStatus: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !record) {
      return;
    }
    setFormState({
      logoUrl: record.logo_url || '',
      webRedirectionSecret: '',
      isSecretEditable: false,
      aaOnboard: Boolean(record.status),
      channelStatus: Boolean(record.channel_status),
    });
    setErrors({});
    setSubmitError(null);
  }, [isOpen, record]);

  const isChannelToggleDisabled = useMemo(
    () => !formState.aaOnboard,
    [formState.aaOnboard]
  );

  const validate = (): boolean => {
    const nextErrors: FormErrors = {};
    if (!formState.logoUrl || !LOGO_URL_REGEX.test(formState.logoUrl)) {
      nextErrors.logoUrl = 'Please enter a valid logo URL.';
    }
    if (formState.isSecretEditable) {
      if (!formState.webRedirectionSecret || !SECRET_REGEX.test(formState.webRedirectionSecret)) {
        nextErrors.webRedirectionSecret = 'Secret key must be 2-30 characters and include only allowed symbols.';
      }
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleToggle = (field: 'aaOnboard' | 'channelStatus') => {
    setFormState((prev) => {
      if (field === 'aaOnboard') {
        const nextAaOnboard = !prev.aaOnboard;
        return {
          ...prev,
          aaOnboard: nextAaOnboard,
          channelStatus: nextAaOnboard ? prev.channelStatus : false,
        };
      }
      if (isChannelToggleDisabled) {
        return prev;
      }
      return {
        ...prev,
        channelStatus: !prev.channelStatus,
      };
    });
  };

  const handleSubmit = async () => {
    if (!record) {
      return;
    }
    setSubmitError(null);
    if (!validate()) {
      return;
    }

    const payload: UpdateAccountAggregatorPayload = {
      account_aggregator_id: record.account_aggregator_id,
      aa_onboard: formState.aaOnboard,
      channel_status: formState.channelStatus,
      logo_url: formState.logoUrl,
    };
    if (formState.isSecretEditable) {
      payload.web_redirection_secret_key = formState.webRedirectionSecret;
    }

    try {
      await onSubmit(payload);
    } catch (error: any) {
      const message = error?.message || 'Unable to update account aggregator.';
      setSubmitError(message);
    }
  };

  if (!isOpen || !record) {
    return null;
  }

  return (
    <>
      <DrawerOverlay />
      <Drawer variant="overlay" width="520px">
        <DrawerHeader className="border-b border-border-default pb-4" onClose={onClose}>
          <div>
            <h2 className="text-xl font-semibold text-text-primary">
              Edit {record.account_aggregator_name} details
            </h2>
            <p className="text-sm text-text-secondary mt-1">
              Update branding and onboarding configuration
            </p>
          </div>
        </DrawerHeader>
        <DrawerContent className="p-6 space-y-5 overflow-y-auto flex-1">
          <InputField
            label="Logo URL"
            type="text"
            value={formState.logoUrl}
            onChange={(event) =>
              setFormState((prev) => ({ ...prev, logoUrl: event.target.value }))
            }
            disabled={isSubmitting}
            error={errors.logoUrl}
            placeholder="https://example.com/logo.png"
          />

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-text-secondary">
                Web Redirection Secret Key
              </label>
              <button
                type="button"
                className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors"
                onClick={() =>
                  setFormState((prev) => ({
                    ...prev,
                    isSecretEditable: !prev.isSecretEditable,
                    webRedirectionSecret: '',
                  }))
                }
                disabled={isSubmitting}
              >
                <Edit3 className="w-4 h-4" />
                {formState.isSecretEditable ? 'Cancel edit' : 'Enable edit'}
              </button>
            </div>
            <InputField
              type="text"
              value={formState.webRedirectionSecret}
              onChange={(event) =>
                setFormState((prev) => ({
                  ...prev,
                  webRedirectionSecret: event.target.value,
                }))
              }
              disabled={isSubmitting || !formState.isSecretEditable}
              error={errors.webRedirectionSecret}
              placeholder={formState.isSecretEditable ? 'Enter new secret key' : 'Secret key hidden'}
              inputClassName={!formState.isSecretEditable ? 'bg-gray-100' : ''}
            />
          </div>

          <div className="flex flex-col gap-4">
            <Toggle
              title="AA Onboarding Status"
              description="Toggle the primary onboarding availability"
              enabled={formState.aaOnboard}
              onToggle={() => handleToggle('aaOnboard')}
              disabled={isSubmitting}
            />
            <Toggle
              title="AA Channel Onboarding Status"
              description="Requires AA onboarding to be active"
              enabled={formState.channelStatus}
              onToggle={() => handleToggle('channelStatus')}
              disabled={isSubmitting || isChannelToggleDisabled}
            />
          </div>

          {submitError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {submitError}
            </div>
          )}
        </DrawerContent>
        <div className="px-6 py-4 border-t border-border-default flex justify-end gap-3 bg-background-secondary flex-shrink-0">
          <Button variant="secondary" size="md" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" size="md" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Update'}
          </Button>
        </div>
      </Drawer>
    </>
  );
};

